import { isAxiosError } from 'axios';
import { api } from '@shared/api';

/**
 * «Грамматика» (2026-10-23, контракт — docs/product/grammar-spec.md § 9): уроки бесплатны, упражнение №1 бесплатно,
 * №2–4 открываются за жетоны один раз; свободные ответы проверяет разбор ИИ (бесплатно несколько на упражнение, дальше
 * дозой за жетон). Ответы API приходят в snake_case (интерцептор `snakeizeKeys`).
 */

export type GrammarLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export interface GrammarAttribution {
  en: string;
  ru: string;
  url: string;
}

export interface GrammarLevelInfo {
  id: GrammarLevel;
  title: string;
}

export interface GrammarPrices {
  free_exercises: number;
  exercise_price: number;
  free_analyses: number;
  analysis_dose: number;
  analysis_dose_price: number;
}

export interface GrammarTopicLink {
  slug: string;
  title: string;
  level: GrammarLevel;
  has_lesson: boolean;
}

export interface GrammarTopicProgress {
  opened: boolean;
  /** Тема пройдена — упражнение №1 завершено (2026-10-23). Одно правило «пройдена» везде (2026-10-26). */
  passed?: boolean;
  /** Урок дочитан до последнего шага (grammar-completion-tz § 8, B2); старый сервер поля не отдаёт. */
  lesson_done?: boolean;
  exercises_total: number;
  exercises_open: number;
  exercises_done: number;
  items_total: number;
  items_solved: number;
}

export interface GrammarTopic {
  slug: string;
  level: GrammarLevel;
  order: number;
  title: string;
  /** Английский термин темы («present simple») — рядом с русским названием. */
  term_en: string;
  summary: string;
  status: 'draft' | 'ready';
  /** Сложная тема: за прохождение — отдельный жетон (решение владельца 2026-10-23). */
  hard?: boolean;
  has_lesson: boolean;
  requires: GrammarTopicLink[];
  contrasts: GrammarTopicLink[];
  see_also: GrammarTopicLink[];
  progress: GrammarTopicProgress;
  /** Тема освоена: «Сложное» завершено и повтор без ошибок через неделю (2026-10-25). */
  mastered?: boolean;
  /** День МСК, с которого пройденная тема готова к повтору; null — не пройдена. */
  review_due?: string | null;
}

/** Награды за темы (2026-10-23): каждые `topics_step` пройденных — `step_tokens`, сложная — `hard_tokens`. */
export interface GrammarRewards {
  topics_step: number;
  step_tokens: number;
  hard_tokens: number;
  passed: number;
  next_at: number;
}

/** Правило «освоена» из настроек сервера (grammar-completion-tz § 8, B3): номер упражнения и дни до повтора. */
export interface GrammarMastery {
  exercise_no: number;
  days: number;
}

export interface GrammarTopics {
  attribution: GrammarAttribution;
  levels: GrammarLevelInfo[];
  prices: GrammarPrices;
  rewards?: GrammarRewards;
  /** Нет поля (старый сервер) — «Сложное» (№ 3) и 7 дней. */
  mastery?: GrammarMastery;
  topics: GrammarTopic[];
  /** «Тебе стоит пройти»: непройденные темы со свежими ошибками, больше ошибок — выше (2026-10-25). */
  recommended?: { slug: string; title: string; mistakes: number }[];
}

/** Подборка повторения (владелец 2026-10-25, grammar-spec.md § 13): темы к повтору и закрытые задания из них. */
export interface GrammarReviewTopic {
  slug: string;
  title: string;
  /** due — подошёл срок повтора; mistakes — свежие ошибки по теме. */
  reason: 'due' | 'mistakes';
  mistakes: number;
  stage: number;
}

export interface GrammarReviewItem extends GrammarItem {
  /** `<slug>:<номер упражнения>:<id задания>` — для ответа. */
  key: string;
  topic: string;
}

export interface GrammarReview {
  available: boolean;
  done_today: boolean;
  reward: number;
  granted: boolean;
  next_due: string | null;
  topics: GrammarReviewTopic[];
  items: GrammarReviewItem[];
}

export interface GrammarReviewAnswer {
  correct: boolean;
  right: string | null;
  explain: string;
  done: boolean;
  reward: { amount: number } | null;
}

export type GrammarLessonBlock =
  | { type: 'text'; text: string }
  | { type: 'tip'; text: string }
  /** `hl` — что выделить в примере (am, is, are). */
  | { type: 'examples'; items: { en: string; ru: string; note: string; hl?: string }[] }
  | { type: 'table'; head: string[]; rows: string[][] }
  /** Как то же сказать по-русски и по-английски; `[]` в `ru` — место, где по-русски ничего не говорят. */
  | { type: 'compare'; rows: { ru: string; en: string; hl?: string }[] };

export interface GrammarMistake {
  wrong: string;
  right: string;
  why: string;
}

/** «Проверь себя» в шаге урока: проверяется сразу на клиенте, бесплатно и без ИИ. */
export interface GrammarCheck {
  prompt: string;
  options: string[];
  answer: string;
  right: string;
  wrong: string;
}

/** Шаг урока — одна мысль: объяснение, «Проверь себя», строка в шпаргалку, частые ошибки этого шага. */
export interface GrammarLessonStep {
  title: string;
  blocks: GrammarLessonBlock[];
  check: GrammarCheck | null;
  remember: string;
  mistakes: GrammarMistake[];
}

/** Урок по шагам (2026-10-23, вариант A): первый шаг — «Главное», дальше разделы. */
export interface GrammarLesson {
  core: GrammarLessonStep;
  sections: GrammarLessonStep[];
}

export interface GrammarLessonResponse {
  topic: GrammarTopic;
  /** null — урок ещё не написан (черновик, виден только админу). */
  lesson: GrammarLesson | null;
}

export type GrammarVerdict = 'correct' | 'almost' | 'wrong';
export type GrammarItemType = 'choice' | 'fill' | 'fix' | 'translate' | 'write';

export interface GrammarAnalyses {
  free_total: number;
  free_left: number;
  paid_left: number;
  left: number;
  dose: number;
  dose_price: number;
  doses_bought: number;
}

export interface GrammarItem {
  id: string;
  type: GrammarItemType;
  prompt: string;
  options: string[] | null;
  example: string | null;
  /** `instant` — проверка сразу и бесплатно; `analysis` — эталон или разбор ИИ. */
  check: 'instant' | 'analysis';
  state: { tries: number; solved: boolean; last: GrammarVerdict | null } | null;
}

/** Сложность упражнения: лёгкое → среднее → сложное → мастер (2026-10-25). */
export type GrammarExerciseLevel = 'easy' | 'medium' | 'hard' | 'master';

export interface GrammarExercise {
  no: number;
  level: GrammarExerciseLevel;
  title: string;
  intro: string;
  free: boolean;
  price: number;
  open: boolean;
  items_total: number;
  items_answered: number;
  items_solved: number;
  completed: boolean;
  analyses: GrammarAnalyses;
  /** null — упражнение закрыто. */
  items: GrammarItem[] | null;
}

export interface GrammarExercises {
  topic: GrammarTopicLink;
  prices: GrammarPrices;
  exercises: GrammarExercise[];
}

export interface GrammarFix {
  wrong: string;
  right: string;
  why: string;
  topic: string | null;
}

export interface GrammarAnswer {
  item_id: string;
  verdict: GrammarVerdict;
  solved: boolean;
  right: string | null;
  explain: string;
  checked_by: 'rule' | 'ai' | 'cache';
  analysis: { corrected: string; comment: string; fixes: GrammarFix[] } | null;
  analysis_spent: 'free' | 'paid' | null;
  analyses: GrammarAnalyses;
  exercise: { items_total: number; items_answered: number; items_solved: number; completed: boolean };
  /** Что выдал этот ответ: за каждые N пройденных тем и за сложную тему; null — ничего. */
  reward?: GrammarReward | null;
  /** После этого ответа упражнение №1 темы завершено — тема пройдена (grammar-completion-tz § 8, B1). */
  topic_passed?: boolean;
  /** Этот ответ впервые завершил упражнение №1 — бывает один раз за всё время. */
  just_passed?: boolean;
  /** Круг наград уже с этой темой — только вместе с `just_passed`. */
  rewards?: GrammarRewards | null;
}

export interface GrammarReward {
  amount: number;
  items: ({ kind: 'topics'; amount: number; passed: number } | { kind: 'hard'; amount: number })[];
}

export async function fetchGrammarReview(): Promise<GrammarReview> {
  const { data } = await api.get<GrammarReview>('/grammar/review');
  return data;
}

export async function answerGrammarReview(key: string, text: string): Promise<GrammarReviewAnswer> {
  const { data } = await api.post<GrammarReviewAnswer>('/grammar/review/answer', { key, text });
  return data;
}

export async function fetchGrammarTopics(): Promise<GrammarTopics> {
  const { data } = await api.get<GrammarTopics>('/grammar/topics');
  return data;
}

export async function fetchGrammarLesson(slug: string): Promise<GrammarLessonResponse> {
  const { data } = await api.get<GrammarLessonResponse>(`/grammar/topics/${encodeURIComponent(slug)}`);
  return data;
}

/** «Урок дочитан» (§ 8, B2): без тела, идемпотентно; только отметка — ни наград, ни влияния на «пройдена». */
export async function markGrammarLessonDone(slug: string): Promise<{ lesson_done: boolean }> {
  const { data } = await api.post<{ lesson_done: boolean }>(`/grammar/topics/${encodeURIComponent(slug)}/lesson-done`);
  return data;
}

export async function fetchGrammarExercises(slug: string): Promise<GrammarExercises> {
  const { data } = await api.get<GrammarExercises>(`/grammar/topics/${encodeURIComponent(slug)}/exercises`);
  return data;
}

export async function openGrammarExercise(slug: string, no: number): Promise<{ charged: number; exercise: GrammarExercise }> {
  const { data } = await api.post<{ charged: number; exercise: GrammarExercise }>(
    `/grammar/topics/${encodeURIComponent(slug)}/exercises/${no}/open`,
  );
  return data;
}

export async function answerGrammarItem(slug: string, no: number, itemId: string, answer: string): Promise<GrammarAnswer> {
  const { data } = await api.post<GrammarAnswer>(`/grammar/topics/${encodeURIComponent(slug)}/exercises/${no}/answers`, {
    item_id: itemId,
    answer,
  });
  return data;
}

export async function buyGrammarAnalyses(
  slug: string,
  no: number,
  requestId: string,
): Promise<{ charged: number; analyses: GrammarAnalyses }> {
  const { data } = await api.post<{ charged: number; analyses: GrammarAnalyses }>(
    `/grammar/topics/${encodeURIComponent(slug)}/exercises/${no}/analyses`,
    { request_id: requestId },
  );
  return data;
}

export interface GrammarErrorInfo {
  code: string | null;
  message: string | null;
  status: number | null;
  details: Record<string, unknown> | null;
}

/** Конверт ошибок API: `{error: {code, message, details}}` (как у игры). */
export function grammarError(err: unknown): GrammarErrorInfo {
  if (!isAxiosError(err)) return { code: null, message: null, status: null, details: null };
  const body = err.response?.data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | undefined;
  return {
    code: body?.error?.code ?? null,
    message: body?.error?.message ?? null,
    status: err.response?.status ?? null,
    details: body?.error?.details ?? null,
  };
}
